import React from "react";
import { DollarSign, Receipt, TrendingUp, XCircle } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";

import CardAutomacao from "./CardAutomacao";
import CardGrupo from "./CardGrupo";

const fmtMoney = (v) =>
  `R$ ${Number(v || 0).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
const fmtNum = (v) => Number(v || 0).toLocaleString("pt-BR");

// Corpo do resumo. O cabeçalho dele vive na barra do topo da página, junto do
// título, formando uma peça só. Sem rolagem própria: os canais abrem em
// sanfona, um por vez, então a coluna cabe na altura da tela.
function Numero({ icone: Icone, rotulo, valor, detalhe, cor }) {
  return (
    <div className="px-4 py-3 border-b border-border/60">
      <div className="flex items-center gap-1.5 mb-1">
        <Icone className={`w-3.5 h-3.5 ${cor}`} />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {rotulo}
        </span>
      </div>
      <p className={`text-2xl font-black leading-none ${cor}`}>{valor}</p>
      {detalhe ? <p className="text-[11px] text-muted-foreground mt-1">{detalhe}</p> : null}
    </div>
  );
}

// Carregamento com cards (25/10/2026): até o 1º resumo chegar, os números e os
// cards dos canais piscam em cinza — antes aparecia "R$ 0,00" e "0 pedidos".
function EsqueletoPainel() {
  return (
    <div data-e2e="painel-resumo-carregando" aria-busy="true" aria-label="Carregando"
      className="rounded-b-xl border border-t-0 bg-card shadow-sm overflow-hidden">
      {[0, 1].map((i) => (
        <div key={i} className="px-4 py-3 border-b border-border/60 space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
      <div className="grid grid-cols-2 border-b border-border/60">
        {[0, 1].map((i) => (
          <div key={i} className="px-4 py-3 space-y-2"><Skeleton className="h-3 w-16" /><Skeleton className="h-5 w-14" /></div>
        ))}
      </div>
      <div className="p-3 space-y-2">
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-3 w-28 mt-1" />
        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[60px] w-full rounded-xl" />)}
      </div>
    </div>
  );
}

export default function PainelResumo({
  stats, grupos, automacao, carregando, grupoAberto, onAlternarGrupo,
  onFiltrarVariacao, variacaoAtiva,
}) {
  if (!stats && carregando) return <EsqueletoPainel />;
  return (
    <div
      data-e2e="painel-resumo"
      className={`rounded-b-xl border border-t-0 bg-card shadow-sm overflow-hidden ${
        carregando ? "opacity-60 transition-opacity" : "transition-opacity"
      }`}
    >
      <Numero
        icone={DollarSign}
        rotulo="Faturamento"
        valor={fmtMoney(stats?.faturamentoTotal)}
        detalhe="Sem cancelados e sem agendados"
        cor="text-emerald-600 dark:text-emerald-400"
      />
      <Numero
        icone={TrendingUp}
        rotulo="Ticket médio"
        valor={fmtMoney(stats?.ticketMedio)}
        detalhe="Por pedido concluído"
        cor="text-orange-600 dark:text-orange-400"
      />
      <div className="grid grid-cols-2 border-b border-border/60">
        <div className="px-4 py-3 border-r border-border/60">
          <div className="flex items-center gap-1.5 mb-1">
            <Receipt className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Pedidos
            </span>
          </div>
          <p className="text-lg font-black leading-none">{fmtNum(stats?.total)}</p>
        </div>
        <div className="px-4 py-3">
          <div className="flex items-center gap-1.5 mb-1">
            <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Cancelados
            </span>
          </div>
          <p className="text-lg font-black leading-none text-red-500">{fmtNum(stats?.cancelado)}</p>
        </div>
      </div>

      <div className="p-3 space-y-2">
        <CardAutomacao automacao={automacao} />
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground pt-1">
          Origem dos pedidos
        </p>
        {(grupos || []).map((grupo) => (
          <CardGrupo
            key={grupo.key}
            grupo={grupo}
            aberto={grupoAberto === grupo.key}
            variacaoAtiva={variacaoAtiva}
            onAlternar={() => onAlternarGrupo(grupo.key)}
            onFiltrarVariacao={onFiltrarVariacao}
          />
        ))}
      </div>
    </div>
  );
}
